import { buildContext, computeRecipeCost, type CostingContext } from './costing';
import type { Ingredient, Recipe } from './types';

export interface IngredientCostShare {
  ingredientId: string;
  name: string;
  cost: number;
  recipeIds: string[];
}
/** Includes purchase units, waste, yields and safety margins at every recipe level. */
export function ingredientCostShares(recipeId: string, ingredients: Ingredient[], recipes: Recipe[]): IngredientCostShare[] {
  const ctx = buildContext(ingredients, recipes);
  const shares = new Map<string, IngredientCostShare>();
  const walk = (id: string, allocated: number, path: string[], context: CostingContext) => {
    const cost = computeRecipeCost(id, context); // validates missing sources and cycles
    for (const line of cost.lines) {
      const amount = cost.subtotal ? allocated * line.cost / cost.subtotal : 0;
      if (line.ref.kind === 'recipe') walk(line.ref.recipeId, amount, [...path, line.ref.recipeId], context);
      else {
        const key = line.ref.ingredientId;
        const share = shares.get(key) ?? { ingredientId:key, name:line.name, cost:0, recipeIds:[] };
        share.cost += amount;
        share.recipeIds = [...new Set([...share.recipeIds, ...path])];
        shares.set(key, share);
      }
    }
  };
  walk(recipeId, computeRecipeCost(recipeId, ctx).costPerPortion, [], ctx);
  return [...shares.values()];
}
export function compareIngredientCosts(before: IngredientCostShare[], after: IngredientCostShare[]) {
  const old = new Map(before.map(x => [x.ingredientId,x]));
  const current = new Map(after.map(x => [x.ingredientId,x]));
  return [...new Set([...old.keys(), ...current.keys()])].map(id => {
    const a = old.get(id); const b = current.get(id);
    const delta = (b?.cost ?? 0) - (a?.cost ?? 0);
    return { ...(b ?? a)!, before:a?.cost ?? 0, after:b?.cost ?? 0, delta, deltaPct:a?.cost ? delta / a.cost * 100 : null };
  }).filter(x => Math.abs(x.delta) > 0.00005).sort((a,b) => Math.abs(b.delta) - Math.abs(a.delta));
}
